const GEO_COOKIE_KEY = 'arabcap_geo_country';
const GEO_COOKIE_MAX_AGE_DAYS = 30;
const GEO_FETCH_TIMEOUT_MS = 4000;

export const PLAN_PRICES = {
  mad: {
    starter: { amount: '99', currencyLabel: 'MAD', display: '99 MAD', perVideo: '12.4 MAD per video' },
    pro: { amount: '189', currencyLabel: 'MAD', display: '189 MAD', perVideo: '6.3 MAD per video' },
    max: { amount: '399', currencyLabel: 'MAD', display: '399 MAD', perVideo: '3.9 MAD per video' },
  },
  usd: {
    starter: { amount: '12', currencyLabel: 'USD', display: '$12', perVideo: '$1.50 per video' },
    pro: { amount: '24', currencyLabel: 'USD', display: '$24', perVideo: '$0.80 per video' },
    max: { amount: '45', currencyLabel: 'USD', display: '$45', perVideo: '$0.45 per video' },
  },
};

function normalizeCountry(code) {
  if (!code || typeof code !== 'string') return null;
  const upper = code.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(upper) ? upper : null;
}

function cookieDomainAttribute() {
  try {
    const host = window.location.hostname || '';
    // Share geo between arabcap.co and app.arabcap.co (not on localhost)
    if (host === 'arabcap.co' || host.endsWith('.arabcap.co')) {
      return '; Domain=.arabcap.co';
    }
  } catch {
    // ignore
  }
  return '';
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

function writeCookie(name, value, maxAgeDays = GEO_COOKIE_MAX_AGE_DAYS) {
  if (typeof document === 'undefined') return;
  const maxAge = Math.floor(maxAgeDays * 24 * 60 * 60);
  const secure = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
  const domain = cookieDomainAttribute();
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/${domain}; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

export function persistCountry(country) {
  const normalized = normalizeCountry(country);
  if (!normalized) return null;
  try {
    writeCookie(GEO_COOKIE_KEY, normalized);
  } catch {
    // ignore
  }
  try {
    localStorage.setItem(GEO_COOKIE_KEY, normalized);
  } catch {
    // ignore
  }
  return normalized;
}

/** Clear cached geo country (cookie + localStorage). */
export function clearCachedCountry() {
  try {
    writeCookie(GEO_COOKIE_KEY, '', 0);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(GEO_COOKIE_KEY);
  } catch {
    // ignore
  }
}

/** Sync read of previously detected country (cookie first, then localStorage). */
export function getCachedCountry() {
  try {
    const fromCookie = normalizeCountry(readCookie(GEO_COOKIE_KEY));
    if (fromCookie) return fromCookie;
  } catch {
    // ignore
  }
  try {
    return normalizeCountry(localStorage.getItem(GEO_COOKIE_KEY));
  } catch {
    return null;
  }
}

/** Timezone → country soft fallback when IP APIs are blocked. */
const TIMEZONE_TO_COUNTRY = {
  'Africa/Casablanca': 'MA',
  'Africa/El_Aaiun': 'MA',
  'Africa/Algiers': 'DZ',
  'Africa/Tunis': 'TN',
  'Africa/Tripoli': 'LY',
  'Africa/Cairo': 'EG',
  'Africa/Khartoum': 'SD',
  'Asia/Riyadh': 'SA',
  'Asia/Dubai': 'AE',
  'Asia/Muscat': 'OM',
  'Asia/Kuwait': 'KW',
  'Asia/Bahrain': 'BH',
  'Asia/Qatar': 'QA',
  'Asia/Aden': 'YE',
  'Asia/Beirut': 'LB',
  'Asia/Damascus': 'SY',
  'Asia/Amman': 'JO',
  'Asia/Hebron': 'PS',
  'Asia/Gaza': 'PS',
  'Asia/Baghdad': 'IQ',
};

function countryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return TIMEZONE_TO_COUNTRY[tz] || null;
  } catch {
    return null;
  }
}

async function fetchWithTimeout(url, ms = GEO_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { cache: 'no-store', signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function detectCountryFromCountryIs() {
  const res = await fetchWithTimeout('https://api.country.is/');
  if (!res.ok) return null;
  const data = await res.json();
  return normalizeCountry(data?.country);
}

async function detectCountryFromIpInfo() {
  const res = await fetchWithTimeout('https://ipinfo.io/json');
  if (!res.ok) return null;
  const data = await res.json();
  return normalizeCountry(data?.country);
}

/**
 * Detect country in the browser (client IP), then persist in a cookie.
 * Uses cookie/localStorage on later visits so we don't re-detect every time.
 * Pass `{ force: true }` to ignore cache (e.g. after clearing ?geo=).
 */
export async function detectCountry({ force = false } = {}) {
  if (!force) {
    const cached = getCachedCountry();
    if (cached) return cached;
  }

  const detectors = [detectCountryFromCountryIs, detectCountryFromIpInfo];

  for (const detect of detectors) {
    try {
      const country = await detect();
      if (country) return persistCountry(country);
    } catch {
      // try next source
    }
  }

  const tzCountry = countryFromTimezone();
  if (tzCountry) return persistCountry(tzCountry);

  return null;
}

/**
 * Prefer subscription billing_currency when present.
 * Morocco (MA) → mad. Unknown/failed geo → mad. Other countries → usd.
 */
export function getDisplayCurrency({ billingCurrency = null, geoCountry = null } = {}) {
  if (billingCurrency === 'mad' || billingCurrency === 'usd') {
    return billingCurrency;
  }
  const country = geoCountry || getCachedCountry();
  if (!country) return 'mad';
  if (country === 'MA') return 'mad';
  return 'usd';
}

export function getPlanPrice(planType, currency = 'mad') {
  const prices = PLAN_PRICES[currency] || PLAN_PRICES.mad;
  return prices[planType] || prices.starter;
}

export function getAllPlanPrices(currency = 'mad') {
  return PLAN_PRICES[currency] || PLAN_PRICES.mad;
}
